<template>
    <div>
        <v-container>
            <v-row>
                <v-col cols="12" md="4" xs="12" sm="12">

                    <v-chip class="ma-2 text-right" label
                            color="default">
                        <v-icon left>
                            mdi-table
                        </v-icon>Table Chart Builder
                    </v-chip>

                    <v-text-field v-model="title"
                                  :rules="titleRules"
                                  :counter="200"
                                  label="Chart Title"
                                  required></v-text-field>

                    <v-row>
                        <v-col cols="12" md="8" xs="12" sm="12">
                            <v-select :items="tableColumns"
                                      item-text="friendlyName"
                                      return-object
                                      v-model="selectedColumn"
                                      label="Source"></v-select>

                        </v-col>
                        <v-col cols="12" md="4" xs="12" sm="12">

                            <v-btn outlined
                                   rounded
                                   class="mt-4"
                                   @click="onAddColumn"
                                   text>
                                Add
                            </v-btn>

                        </v-col>
                    </v-row>

                    <div class="mb-3">
                        <v-chip v-for="(column, index) in chartColumns"
                                class="ma-2"
                                close
                                v-bind:key="`chip${index}`"
                                color="primary"
                                label
                                @click:close="onRemoveSelectedColumn(column)"
                                outlined>
                            {{ column.friendlyName }}
                        </v-chip>
                    </div>

                    <v-expansion-panels>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Date Range
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-slider v-model="selectedDays"
                                          class="mt-6"
                                          max="365"
                                          thumb-label="always"></v-slider>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                Chart Filters
                                <span class="ml-1" v-if="chartFilters.length > 0">
                                    ({{chartFilters.length}})
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>

                                <p class="caption mb-4">Select a source and apply any given filters to the chart, you can add more than one filter at a time.</p>

                                <v-select :items="filterableTableColumns"
                                          item-text="friendlyName"
                                          return-object
                                          v-model="selectedFilterColumn"
                                          label="Source"></v-select>

                                <v-select v-model="selectedColumnFilters"
                                          :items="sourceItems"
                                          attach
                                          chips
                                          label="Chart Filters"
                                          multiple>
                                    <template v-slot:append>
                                        <v-chip color="primary" @click="onOpenNewFilterOption" v-if="selectedFilterColumn" class="ma-2">
                                            <v-icon>
                                                mdi-plus
                                            </v-icon> Add New Item
                                        </v-chip>
                                    </template>
                                    <template v-slot:prepend-item>
                                        <v-list-item ripple
                                                     @click="toggleSourceItems">
                                            <v-list-item-action>
                                                <v-icon :color="selectedColumnFilters.length > 0 ? 'primary darken-4' : ''">
                                                    {{ sourceIcon }}
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    Select All
                                                </v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider class="mt-2"></v-divider>
                                    </template>

                                </v-select>

                                <v-row>
                                    <v-col class="text-right">
                                        <v-btn outlined
                                               rounded
                                               class="mt-4"
                                               @click="onAddColumnFilter"
                                               text>
                                            Add Filter
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <v-divider v-if="chartFilters.length > 0" class="mt-4 mb-4"></v-divider>

                                <v-card v-for="(filter, index) in chartFilters" :key="filter.name" class="pt-4 mt-4 pa-2">
                                    <v-row>
                                        <v-col cols="12" md="4" lg="4">
                                            <div class="caption">Filter {{index + 1}}</div>
                                            {{filter.friendlyName}}
                                        </v-col>
                                        <v-col cols="12" md="6" lg="6">
                                            <v-chip class="ma-1" v-for="name in filter.columnFilters" :key="name">
                                                {{name}}
                                            </v-chip>
                                        </v-col>
                                        <v-col cols="12" md="2" lg="2">
                                            <v-btn outlined
                                                   rounded
                                                   small
                                                   @click="onRemoveColumnFilter(filter.reportTableColumnId)"
                                                   class="mt-4"
                                                   text>
                                                <v-icon dark>
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-row class="mt-4" v-if="isAdmin">
                        <v-col cols="12" md="6" xl="6">
                            <p class="caption mb-4">If True the chart will be part of the template meaning it can be used for all reports. or false means only you will be able to view and edit the chart in the "my charts" page.</p>
                        </v-col>
                        <v-col cols="12" md="6" xl="6">
                            <v-switch v-if="isAdmin" v-model="isTemplate"
                                      label="Is Template"></v-switch>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="8" xs="12" sm="12">
                    <v-skeleton-loader v-show="isBusy"
                                       type="image"></v-skeleton-loader>

                    <v-card style="min-height:400px;" v-show="!isBusy">
                        <table-chart ref="tableComponent"
                                     v-show="chartColumns.length > 0"
                                     :headers="tablePreviewHeaders"
                                     :table-filter="tableFilter"
                                     :height-v="heightV"
                                     :data="tablePreviewData"></table-chart>
                    </v-card>
                </v-col>
                <v-col cols="12" md="8" xs="12" sm="12">
                    <v-switch v-model="ignoreDateFilter"
                              label="Ignore Date Filter (Report Filters)"></v-switch>
                </v-col>
            </v-row>
        </v-container>

        <sweet-modal ref="filterModal" :modal-theme="$vuetify.theme.dark === true ? 'dark' : 'light'" :overlay-theme="$vuetify.theme.dark === true ? 'light' : 'dark'" blocking>

            <v-form v-model="isFilterFormValid" ref="filterForm">
                <v-container>
                    <v-row>
                        <v-col cols="12"
                               md="12">
                            <v-text-field v-model="customFilter"
                                          :rules="filterNameRules"
                                          :counter="200"
                                          label="Filter"
                                          required></v-text-field>
                        </v-col>

                    </v-row>
                    <v-row>
                        <v-col cols="12" align="right">
                            <v-btn depressed
                                   :disabled="!isFilterFormValid"
                                   align="right"
                                   @click="onAddNewFilter"
                                   class="black--text mt-10 font-bold"
                                   color="primary">
                                Add
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-form>
        </sweet-modal>
    </div>
</template>

<script>
    import TableChart from "./TableChart"
    import config from '../../../public/okta.config.json';
    import reportClient from "../../services/reportService"
    import chartService from "../../services/chartService"
    import ReportTableService from "../../services/reportTableService"
    export default {
        name: 'TableChartBuilder',
        props: {

        },
        components: {
            TableChart
        },
        data: function () {
            return {
                isFilterFormValid: false,
                customFilter: "",
                isAdmin: false,
                isTemplate: true,
                accessToken: "",
                isBusy: false,
                title: "",
                titleRules: [
                    v => !!v || 'Title is required',
                    v => v.length <= 200 || 'Title must be less than 200 characters'],
                filterNameRules: [
                    v => !!v || 'Filter is required',
                    v => v.length <= 200 || 'Filter must be less than 200 characters'],
                chartColumns: [],
                selectedDays: 7,
                tableColumns: [],
                sourceItems: [],
                originalTableColumns: [],
                tablePreviewHeaders: [],
                tablePreviewData: [],
                selectedColumn: null,
                tableFilter: {
                    searchText: "",
                    pageNo: 1,
                    pageSize: 5,
                    orderBy: "incidentStartDate",
                    sortDesc: true,
                    totalItems: 0,
                },
                heightV: 300,
                ignoreDateFilter: false,
                chartFilters: [],
                selectedFilterColumn: null,
                selectedColumnFilters: [],
            }
        },
        created() {
            this.setup();
        },
        watch: {
            deep: true,
            immediate: true,
            chartColumns() {
                this.getTableChart();
            },
            selectedDays() {
                this.getTableChart();
            },
            selectedFilterColumn() {
                this.getUniqueSourceItems();
            },
            chartFilters() {
                this.getTableChart();
            }
        },
        computed: {
            filterableTableColumns() {
                return this.originalTableColumns.filter(column => column.canFilter);
            },
            sourceAllOptions() {
                return this.selectedColumnFilters.length === this.sourceItems.length
            },
            sourceSomeOptions() {
                return this.selectedColumnFilters.length > 0 && !this.sourceAllOptions
            },
            sourceIcon() {
                if (this.sourceAllOptions) return 'mdi-close-box'
                if (this.sourceSomeOptions) return 'mdi-minus-box'
                return 'mdi-checkbox-blank-outline'
            }
        },
        methods: {
            async setup() {
                this.accessToken = await this.$auth.getAccessToken();

                var user = await this.$auth.getUser();
                this.isAdmin = user.groups.includes(config.adminGroup);

                await this.getReportTableColumns();

                if (this.$route.params.id && this.$route.params.type === 'TABLE') {
                    await this.load(this.$route.params.id);
                }
            },
            async load(chartId) {
                if (chartId) {
                    this.isBusy = true;
                    let result = await chartService.getTableChartDetailsById(chartId, this.accessToken);

                    this.isTemplate = result.isTemplate;

                    this.title = result.title;

                    result.chartColumns.forEach((item) => {
                        this.chartColumns.push(item);
                    });
                    result.chartFilters.forEach((item) => {
                        this.chartFilters.push(item);
                    });

                    this.selectedDays = result.days;
                    this.ignoreDateFilter = result.ignoreDateFilter;
                    this.isBusy = false;
                }
            },
            toggleSourceItems() {
                this.$nextTick(() => {
                    if (this.sourceAllOptions) {
                        this.selectedColumnFilters = []
                    } else {
                        this.selectedColumnFilters = this.sourceItems.slice()
                    }
                })
            },
            async getTableChart() {
                if (!this.isBusy) {
                    this.tablePreviewHeaders = [];
                    this.tablePreviewData = [];
                    let chartRequest = {
                        chartColumns: this.chartColumns,
                        chartFilters: this.chartFilters,
                        days: this.selectedDays
                    }

                    this.isBusy = true;

                    let results = await reportClient.getTableChart(
                        chartRequest,
                        this.accessToken);

                    if (results && results.length > 0) {

                        const keys = Object.keys(results[0]);

                        this.tablePreviewHeaders = [];
                        keys.forEach((item) => {
                            let selectedColumn = this.tableColumns.find((tc) => tc.columnName.toLowerCase() === item.toLowerCase());
                            if (selectedColumn) {
                                this.tablePreviewHeaders.push({
                                    text: selectedColumn.friendlyName,
                                    align: 'start',
                                    sortable: true,
                                    value: this.camelCaseString(selectedColumn.columnName)
                                });
                            }
                        });

                        this.tablePreviewData = results;
                        this.tableFilter.totalItems = results.length;
                    }

                    this.isBusy = false;
                }
            },
            getChartDetails() {
                return {
                    chartColumns: this.chartColumns,
                    chartFilters: this.chartFilters,
                    days: this.selectedDays,
                    title: this.title,
                    isTemplate: this.isTemplate && this.isAdmin,
                    ignoreDateFilter: this.ignoreDateFilter,
                };
            },
            camelCaseString(string) {
                return string.charAt(0).toLowerCase() + string.slice(1);
            },
            onAddColumn() {
                if (!this.chartColumns.find((column) => column.columnName === this.selectedColumn.columnName)) {
                    this.chartColumns.push(this.selectedColumn);
                }
            },
            onRemoveSelectedColumn(item) {
                this.chartColumns = this.chartColumns.filter((clm) => clm.columnName !== item.columnName);
            },
            async getUniqueSourceItems() {
                if (this.selectedFilterColumn && !this.chartFilters.some(filter => filter.columnName === this.selectedFilterColumn.columnName)) {
                    this.selectedColumnFilters = [];
                    this.sourceItems = [];
                    var result = await reportClient.getUniqueSourceItems(
                        this.selectedFilterColumn.id,
                        this.accessToken);
                    for (var i = 0; i < result.length; i++) {
                        this.selectedColumnFilters.push(result[i]);
                    }
                    this.sourceItems = result;
                }
            },
            async getReportTableColumns() {

                let result = await ReportTableService.searchReportTableColumns(
                    "",
                    "",
                    1,
                    100,
                    "ColumnName",
                    "ASCENDING",
                    this.accessToken);

                if (result && result.data) {
                    this.originalTableColumns = result.data.map(obj => ({ ...obj, reportTableColumnId: obj.id }));
                    this.tableColumns = result.data.map(obj => ({ ...obj, reportTableColumnId: obj.id }));
                }

            },
            async onAddColumnFilter() {

                var chartFilter = {
                    reportTableColumnId: this.selectedFilterColumn.id,
                    columnName: this.selectedFilterColumn.columnName,
                    columnFilters: this.selectedColumnFilters,
                    friendlyName: this.selectedFilterColumn.friendlyName
                }

                this.chartFilters.push(chartFilter);

                this.selectedFilterColumn = null;
                this.selectedColumnFilters = [];

                await this.getTableChart();
            },
            onRemoveColumnFilter(id) {
                this.chartFilters = this.chartFilters.filter((item) => item.reportTableColumnId !== id);
            },
            onOpenNewFilterOption() {
                this.$refs.filterModal.open();
                this.customFilter = "";
                this.$refs.filterForm.resetValidation();
            },
            onAddNewFilter() {
                this.sourceItems.push(this.customFilter);
                this.selectedColumnFilters.push(this.customFilter);
                this.customFilter = "";
                this.$refs.filterModal.close();
            }
        },

    }
</script>
